<template>
  <v-container class="h-screen flex items-center justify-center bg-gray-50/50" fluid>
    <v-row justify="center" align="center" class="text-center">
      <v-col cols="12" md="8" lg="6">
        <div class="mb-8 flex justify-center">
          <div class="bg-amber-50 p-8 rounded-full ring-8 ring-amber-50/50">
            <v-icon :icon="mdiCloudOffOutline" size="100" class="text-amber-600" />
          </div>
        </div>

        <h1 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          Não foi possível carregar suas permissões
        </h1>

        <p
          class="text-gray-500 text-lg mb-2 leading-relaxed max-w-lg mx-auto"
          data-testid="permissions-unavailable-message"
        >
          {{ message }}
        </p>
        <p class="text-gray-500 mb-10 leading-relaxed max-w-lg mx-auto">
          Por segurança, o acesso fica bloqueado até elas carregarem.
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center w-full">
          <v-btn
            color="primary"
            class="w-full sm:w-auto h-[56px] font-bold capitalize px-8 text-white"
            elevation="0"
            :loading="isRetrying"
            data-testid="permissions-unavailable-retry-button"
            @click="retry"
          >
            Tentar novamente
          </v-btn>
          <v-btn
            variant="outlined"
            color="primary"
            class="w-full sm:w-auto h-[56px] font-bold capitalize px-8"
            elevation="0"
            :disabled="isRetrying"
            data-testid="permissions-unavailable-logout-button"
            @click="authStore.logout()"
          >
            Sair
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { mdiCloudOffOutline } from '@mdi/js'
import { useErrorToast } from '@/composables/useErrorToast'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { PERMISSIONS_LOAD_ERROR, usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import PATHS from '@/router/paths'
import { PERMISSIONS_UNAVAILABLE_ROUTE_NAME, resolveRetryTarget } from './retryTarget'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()
const { loadError } = storeToRefs(permissionsStore)
const { notifyErrorMessage } = useErrorToast()

// A store zera o `loadError` enquanto o retry voa; a tela mantém o último erro
// visível em vez de piscar a mensagem padrão.
const lastError = ref(loadError.value)
watch(loadError, (value) => {
  if (value) lastError.value = value
})
const message = computed(() => lastError.value ?? PERMISSIONS_LOAD_ERROR)
const isRetrying = ref(false)

onMounted(() => {
  // Sem falha registrada (acesso direto com as permissões carregadas, ou sem
  // sessão): não há o que tentar de novo. O guard do dashboard decide o resto.
  if (!loadError.value) router.replace(PATHS.DASHBOARD)
})

function retryTarget(): string {
  const target = resolveRetryTarget(route.query.redirect)
  // Variacoes do proprio caminho (barra final, caixa) casam nesta mesma rota e
  // prenderiam o usuario aqui depois de um retry bem-sucedido.
  return router.resolve(target).name === PERMISSIONS_UNAVAILABLE_ROUTE_NAME
    ? PATHS.DASHBOARD
    : target
}

async function retry() {
  if (isRetrying.value) return
  isRetrying.value = true
  try {
    await permissionsStore.fetchPermissions()
    // Falhou de novo: fica aqui, já com a mensagem da nova tentativa. O toast
    // avisa mesmo quando o texto é idêntico ao anterior. `fetchPermissions` não
    // rejeita — o erro já chega como mensagem exibível em `loadError`.
    if (permissionsStore.loadError) {
      notifyErrorMessage(permissionsStore.loadError)
      return
    }
    // O guard da rota de destino ainda confere as permissões recém-carregadas.
    await router.replace(retryTarget())
  } finally {
    isRetrying.value = false
  }
}
</script>
